// The head tags a shared link needs, for every public route, defined once.
//
// Two consumers read this and they must agree exactly:
//
//   - RouteSeo.tsx renders these through react-helmet-async, for browsers.
//   - scripts/prerender-meta.js writes them into static HTML at build time, for
//     social crawlers. Instagram, Facebook, Reddit, LinkedIn and Slack read the
//     served HTML and never run JavaScript, so without this every page on the site
//     previewed as the homepage. (#814)
//
// It is CommonJS on purpose: the build script runs in plain Node, outside webpack,
// and cannot import TypeScript. Webpack and Jest both read CommonJS happily.
//
// The static copies are for crawlers only. This app runs React 19, and helmet-async
// 3 on React 19 renders head tags as ordinary React elements that React hoists into
// <head>. React does not adopt tags that were already in the HTML: it adds its own
// beside them, and on client-side navigation it updates its own and leaves the
// static ones stale, first in the document. So every static tag carries STATIC_ATTR,
// and removeStaticHead() deletes them before React renders. A crawler never runs
// that code and keeps them; a browser never sees two.

const routes = require('./routeMeta.json');

const SITE = 'https://trychaptery.com';
const IMAGE = `${SITE}/og-image.png`;

/** Marks a head tag written at build time for crawlers, to be removed at boot. */
const STATIC_ATTR = 'data-static-head';

/** Every route with share metadata, in sitemap order. */
const ROUTE_PATHS = routes.map((r) => r.path);

function metaFor(path) {
  const m = routes.find((r) => r.path === path);
  if (!m) throw new Error(`No share metadata for ${path}. Add it to src/seo/routeMeta.json.`);
  return m;
}

/**
 * The page title, which is also the og:title and twitter:title.
 * @param {string} path
 * @returns {string}
 */
function titleFor(path) {
  return metaFor(path).title;
}

/**
 * Every head tag for one page except <title>, as { tag, attrs } in render order.
 *
 * og:description repeats the page description rather than carrying separate copy.
 * One sentence per page is easier to keep true than two, and a share card that
 * says something different from the search result is a small lie in itself.
 *
 * @param {{ path: string, title: string, description: string, type?: string }} page
 * @returns {{ tag: 'meta' | 'link', attrs: Record<string, string> }[]}
 */
function pageTags({ path, title, description, type = 'website' }) {
  const url = SITE + path;
  return [
    { tag: 'meta', attrs: { name: 'description', content: description } },
    { tag: 'link', attrs: { rel: 'canonical', href: url } },
    { tag: 'meta', attrs: { property: 'og:type', content: type } },
    { tag: 'meta', attrs: { property: 'og:url', content: url } },
    { tag: 'meta', attrs: { property: 'og:title', content: title } },
    { tag: 'meta', attrs: { property: 'og:description', content: description } },
    { tag: 'meta', attrs: { property: 'og:image', content: IMAGE } },
    { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { tag: 'meta', attrs: { name: 'twitter:title', content: title } },
    { tag: 'meta', attrs: { name: 'twitter:description', content: description } },
    { tag: 'meta', attrs: { name: 'twitter:image', content: IMAGE } },
  ];
}

/** The head tags for a route in routeMeta.json. */
function headTags(path) {
  const m = metaFor(path);
  return pageTags({ path, title: m.title, description: m.description });
}

module.exports = { SITE, IMAGE, STATIC_ATTR, ROUTE_PATHS, titleFor, headTags, pageTags };
